/**
 * Espejo CLIENTE de la ventana de acceso a la clase en vivo (F3-G). PURO y
 * testeable. La autoridad es el servidor (`GET /api/live/sessions/:id/join`, que
 * valida en la zona horaria de la academia); esto es solo para la UI previa
 * (cuenta atrás, badge "EN VIVO", habilitar el botón "Entrar").
 *
 * Trabaja en HORA LOCAL FLOTANTE: interpreta `date`+`startTime` como hora del
 * dispositivo (igual que el ICS de horario del F3-A). Suficiente para la UI; el
 * servidor decide de verdad.
 */

/** Minutos ANTES del inicio en que abre la ventana (default; = `LIVE_JOIN_WINDOW_MIN`). */
export const DEFAULT_JOIN_OPEN_BEFORE_MIN = 15;
/** Minutos DESPUÉS del fin durante los que sigue permitido el join (default). */
export const DEFAULT_JOIN_GRACE_AFTER_MIN = 60;

/** Fase de la sesión respecto a su ventana de acceso. */
export type JoinWindowPhase = "before" | "open" | "ended";

/** Horas de una sesión (columnas sincronizadas de `class_session`). */
export interface SessionTimes {
  /** `YYYY-MM-DD`. */
  date: string | null | undefined;
  /** `HH:MM[:SS]`. */
  startTime: string | null | undefined;
  /** `HH:MM[:SS]`. */
  endTime: string | null | undefined;
}

/** Estado de la ventana calculado en un instante dado. */
export interface JoinWindowState {
  phase: JoinWindowPhase;
  /** Epoch ms en que abre la ventana (o null si las horas no son válidas). */
  opensAtMs: number | null;
  /** Epoch ms en que cierra la ventana. */
  closesAtMs: number | null;
  /** ms hasta que abre (0 si ya abrió o pasó). */
  msUntilOpen: number;
  /** ¿Se puede entrar AHORA? (phase === "open"). */
  isLiveNow: boolean;
}

/** Normaliza `HH:MM` → `HH:MM:00` y deja `HH:MM:SS` como está. */
function normalizeTime(t: string): string {
  const parts = t.split(":");
  if (parts.length === 2) {
    return `${t}:00`;
  }
  return t;
}

/**
 * Parsea `date`+`time` como hora LOCAL flotante → epoch ms, o `null` si falta o
 * es inválido. `new Date("YYYY-MM-DDTHH:MM:SS")` (sin `Z`) usa la zona local.
 */
export function parseLocalMs(
  date: string | null | undefined,
  time: string | null | undefined,
): number | null {
  if (!date || !time) {
    return null;
  }
  const ms = new Date(`${date}T${normalizeTime(time)}`).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Calcula el estado de la ventana de acceso en `nowMs`. Puro.
 */
export function joinWindowState(
  nowMs: number,
  times: SessionTimes,
  openBeforeMin: number = DEFAULT_JOIN_OPEN_BEFORE_MIN,
  graceAfterMin: number = DEFAULT_JOIN_GRACE_AFTER_MIN,
): JoinWindowState {
  const startMs = parseLocalMs(times.date, times.startTime);
  const endMs = parseLocalMs(times.date, times.endTime);
  if (startMs === null || endMs === null) {
    return { phase: "before", opensAtMs: null, closesAtMs: null, msUntilOpen: 0, isLiveNow: false };
  }
  const opensAtMs = startMs - openBeforeMin * 60_000;
  const closesAtMs = endMs + graceAfterMin * 60_000;

  let phase: JoinWindowPhase;
  if (nowMs < opensAtMs) {
    phase = "before";
  } else if (nowMs <= closesAtMs) {
    phase = "open";
  } else {
    phase = "ended";
  }
  return {
    phase,
    opensAtMs,
    closesAtMs,
    msUntilOpen: Math.max(0, opensAtMs - nowMs),
    isLiveNow: phase === "open",
  };
}

/**
 * Formatea una cuenta atrás (ms) como `Xd Xh Xm Xs` compacto (omite ceros a la
 * izquierda). Puro; útil para el pre-join.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (days > 0 || hours > 0) {
    parts.push(`${hours}h`);
  }
  if (days > 0 || hours > 0 || mins > 0) {
    parts.push(`${mins}m`);
  }
  parts.push(`${secs}s`);
  return parts.join(" ");
}
